import { type OrderBy } from '@inpart-io/devtools-iap';
import { type BoardLayout } from '@inpart-io/padme-iap';
import { type MutationTree } from 'vuex';

import { type IPagination, type ISearchState } from './types';

export const RECEIVE_SEARCH_RESULTS = 'RECEIVE_SEARCH_RESULTS';
export const UPDATE_SEARCH_RESULTS = 'UPDATE_SEARCH_RESULTS';
export const RECENTLY_ADDED_SEARCH_RESULTS = 'RECENTLY_ADDED_SEARCH_RESULTS';
export const RECEIVE_CURRENT_PAGE = 'RECEIVE_CURRENT_PAGE';
export const RECEIVE_PAGE_SIZE = 'RECEIVE_PAGE_SIZE';
export const RECEIVE_VISIBLE_COLUMNS = 'RECEIVE_VISIBLE_COLUMNS';
export const RECEIVE_LAYOUT = 'RECEIVE_LAYOUT';
export const RECEIVE_ROW_COUNT = 'RECEIVE_ROW_COUNT';
export const RECEIVE_ORDER_BY = 'RECEIVE_ORDER_BY';
export const RECEIVE_PAGINATION = 'RECEIVE_PAGINATION';
export const RECEIVE_PAGINATION_TIMESTAMP = 'RECEIVE_PAGINATION_TIMESTAMP';
export const RECEIVE_SEARCH_TIMESTAMP = 'RECEIVE_SEARCH_TIMESTAMP';
export const REMOVE_PAGINATION = 'REMOVE_PAGINATION';
export const REMOVE_SEARCH_RESULT_LIST = 'REMOVE_SEARCH_RESULT_LIST';

export function getMutations(): MutationTree<ISearchState> {
    return {
        [RECEIVE_SEARCH_RESULTS](_state, { key, idList }: { key: string; idList: number[] }) {
            _state.search.set(key, idList);
        },
        [RECENTLY_ADDED_SEARCH_RESULTS](_state, { key, idList, atEnd = false }: { key: string; idList: number[]; atEnd: boolean }) {
            if (idList?.length > 0) {
                const currentList = _state.search.get(key) ?? [];
                const nonDuplicatedIdList = Array.from(new Set<number>(atEnd ? [...currentList, ...idList] : [...idList, ...currentList]));

                _state.search.set(key, nonDuplicatedIdList);
                if (_state.pagination.has(key)) {
                    const rowsDiff = Math.abs(currentList.length - nonDuplicatedIdList.length);
                    const newRowsCount = _state.pagination.get(key).rowsNumber !== undefined ? _state.pagination.get(key).rowsNumber + rowsDiff : idList.length;

                    _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { rowsNumber: newRowsCount }));
                }
            }
        },
        [UPDATE_SEARCH_RESULTS](
            _state,
            { key, idList, listToAdd, listToRemove }: { key: string; idList: number[]; listToAdd?: number[]; listToRemove?: number[] }
        ) {
            const currentList = _state.search.get(key)?.slice() ?? [];
            if (listToRemove?.length > 0) {
                const newRemovedResults =
                    currentList?.length > 0
                        ? currentList.filter((id) => {
                              return !listToRemove.includes(id);
                          })
                        : [];
                const newRowsCount =
                    _state.pagination.get(key)?.rowsNumber > listToRemove.length ? _state.pagination.get(key).rowsNumber - listToRemove.length : 0;

                _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { rowsNumber: newRowsCount }));
                _state.search.set(key, newRemovedResults);
            }

            if (listToAdd?.length > 0) {
                const nonDuplicatedIdList = Array.from(new Set<number>([...currentList, ...listToAdd]));
                const rowsDiff = Math.abs(currentList.length - nonDuplicatedIdList.length);
                const newRowsCount = _state.pagination.get(key).rowsNumber !== undefined ? _state.pagination.get(key).rowsNumber + rowsDiff : listToAdd.length;

                _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { rowsNumber: newRowsCount }));
                _state.search.set(key, nonDuplicatedIdList);
            }

            if (idList?.length > 0) {
                const newSearchResults = currentList?.length > 0 ? Array.from(new Set<number>([...currentList, ...idList])) : idList;
                _state.search.set(key, newSearchResults);
            }
        },
        [RECEIVE_CURRENT_PAGE](_state, { key, currentPage }: { key: string; currentPage: number }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { currentPage }));
        },
        [RECEIVE_PAGE_SIZE](_state, { key, pageSize }: { key: string; pageSize: number }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { pageSize }));
        },
        [RECEIVE_VISIBLE_COLUMNS](_state, { key, visibleColumns }: { key: string; visibleColumns: string[] }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { visibleColumns }));
        },
        [RECEIVE_LAYOUT](_state, { key, layout }: { key: string; layout: BoardLayout }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { layout }));
        },
        [RECEIVE_ROW_COUNT](_state, { key, rowsNumber }: { key: string; rowsNumber: number }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { rowsNumber }));
        },
        [RECEIVE_ORDER_BY](_state, { key, orderBy }: { key: string; orderBy: OrderBy[] }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), { orderBy }));
        },
        [RECEIVE_PAGINATION](_state, { key, pagination }: { key: string; pagination: IPagination }) {
            _state.pagination.set(key, Object.assign({}, _state.pagination.get(key), pagination));
        },
        [REMOVE_PAGINATION](_state, { key }: { key: string }) {
            _state.pagination.delete(key);
        },
        [REMOVE_SEARCH_RESULT_LIST](_state, { key }: { key: string }) {
            _state.search.delete(key);
        },
        [RECEIVE_SEARCH_TIMESTAMP](_state, { timestamp }: { timestamp?: string }) {
            _state.timestamp = timestamp;
        },
        [RECEIVE_PAGINATION_TIMESTAMP](_state, { key, timestamp }: { key: string; timestamp?: string }) {
            if (_state.pagination.get(key) !== undefined) {
                _state.pagination.get(key).timestamp = timestamp;
            }
        }
    };
}
